import {
  CircularProgress,
  CircularProgressProps,
  Fade,
  Portal,
  useTheme,
} from '@material-ui/core';
import styledMixins from 'app/styles/styledMixins';
import { TransientPick } from 'app/tools/general.types';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components/macro';
import { Merge } from 'type-fest';
import {
  useInnerFramePortal,
  useOuterFramePortal,
} from '../context-hooks/use-frame-portal.hook';

const FullscreenContainer = styled.div`
  position: absolute;
  ${styledMixins.fullsize}
  top: 0;
  flex: 1 1 0;
`;
type SpinnerBackgroundOpts = TransientPick<
  Props,
  'translucent' | 'defaultBgColor'
>;
const SpinnerBackground = styled.div<SpinnerBackgroundOpts>`
  ${styledMixins.flexCenter}
  ${styledMixins.fullsize}
  background: ${p =>
    p.$translucent
      ? '#fff8'
      : p.$defaultBgColor
      ? p.theme.palette.background.default
      : '#fff'};
`;

type Props = Merge<
  CircularProgressProps,
  {
    loadingComplete?: boolean;
    translucent?: boolean;
    defaultBgColor?: boolean;
    noPortal?: boolean;
    frame?: 'inner' | 'outer';
    className?: string;
  }
>;
export default function LoadingOverlay({
  loadingComplete = false,
  translucent = false,
  defaultBgColor = false,
  noPortal = false,
  frame = 'inner',
  className,
  ...props
}: Props): JSX.Element | null {
  const [innerContainer] = useInnerFramePortal();
  const [outerContainer] = useOuterFramePortal();
  const appOnline = useSelector(s => s.context.appOnline);
  const theme = useTheme();
  const [hideContainer, setHideContainer] = useState(loadingComplete);

  useEffect(() => {
    if (!loadingComplete) setHideContainer(false);
  }, [loadingComplete]);

  const zIndex = noPortal ? 0 : frame === 'inner' ? 1 : 100;

  const overlay = !hideContainer && (
    <FullscreenContainer
      css={`
        z-index: ${zIndex};
      `}
      {...{ className }}
    >
      <Fade
        in={!loadingComplete && !!appOnline}
        timeout={{
          enter: 0,
          exit: theme.transitions.duration.leavingScreen,
        }}
        onExit={() => {
          setTimeout(() => {
            if (loadingComplete) setHideContainer(true);
          }, 800);
        }}
        onExited={() => {
          if (loadingComplete) setHideContainer(true);
        }}
      >
        <SpinnerBackground
          $translucent={translucent}
          $defaultBgColor={defaultBgColor}
        >
          <Fade in style={{ transitionDelay: '1s' }}>
            <CircularProgress
              size={100}
              css={css`
                ${translucent &&
                !props.color &&
                css`
                  color: white;
                `}
              `}
              {...props}
            />
          </Fade>
        </SpinnerBackground>
      </Fade>
    </FullscreenContainer>
  );
  return noPortal ? (
    <>{overlay}</>
  ) : (
    <Portal
      container={frame === 'inner' ? innerContainer : outerContainer}
    >
      {overlay}
    </Portal>
  );
}
